/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CustomerDepartmentQualificationsListItemResponse } from '../models/customer-department-qualifications-list-item-response';
import { CustomerDepartmentResponse } from '../models/customer-department-response';
import { CustomerInquiryListItemResponse } from '../models/customer-inquiry-list-item-response';
import { DepartmentListItemResponse } from '../models/department-list-item-response';
import { DocumentUploadRequest } from '../models/document-upload-request';
import { GeneralApiResponse } from '../models/general-api-response';
import { ImportUploadResponse } from '../models/import-upload-response';
import { ListApiResponse } from '../models/list-api-response';
import { UserItemResponse } from '../models/user-item-response';

@Injectable({
  providedIn: 'root',
})
export class CustomerDepartmentService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation postCustomerDepartmentImportProcess
   */
  static readonly PostCustomerDepartmentImportProcessPath = '/api/web/v1/customers/{customer}/departments/imports/{import}';

  /**
   * Process a previously uploaded CustomerDepartment import: create departments from the
   * stored file under each row's location, touching (updating the timestamp of) any
   * department that already exists — matched by name within the row's location.
   *
   * The import is addressed by its UUID — a non-guessable handle that prevents enumeration
   * of import records on top of the customer-scoped authorization.
   *
   * **Accessible only for customer admins of the customer in the URL**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postCustomerDepartmentImportProcess()` instead.
   *
   * This method doesn't expect any request body.
   */
  postCustomerDepartmentImportProcess$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;
    import: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerDepartmentService.PostCustomerDepartmentImportProcessPath, 'post');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('import', params.import, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Process a previously uploaded CustomerDepartment import: create departments from the
   * stored file under each row's location, touching (updating the timestamp of) any
   * department that already exists — matched by name within the row's location.
   *
   * The import is addressed by its UUID — a non-guessable handle that prevents enumeration
   * of import records on top of the customer-scoped authorization.
   *
   * **Accessible only for customer admins of the customer in the URL**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `postCustomerDepartmentImportProcess$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  postCustomerDepartmentImportProcess(params: {

    /**
     * Customer Id.
     */
    customer: number;
    import: string;
    context?: HttpContext
  }
): Observable<void> {

    return this.postCustomerDepartmentImportProcess$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation postCustomerDepartmentImportUpload
   */
  static readonly PostCustomerDepartmentImportUploadPath = '/api/web/v1/customers/{customer}/departments/imports';

  /**
   * Upload and validate a filled CustomerDepartment import template (.xlsx or .csv).
   *
   * Validates every row up front — including that each row's location id names a location
   * of THIS customer: any error returns 400 with the full error list and nothing is
   * stored. An already-existing department (same name within the row's location) is NOT an
   * upload error — it is a process-time duplicate whose timestamp gets touched. On success
   * the file is saved and an import record is created (unprocessed) for a later
   * processing call.
   *
   * **Accessible only for customer admins of the customer in the URL**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postCustomerDepartmentImportUpload()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  postCustomerDepartmentImportUpload$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;
    context?: HttpContext

    /**
     * The filled import template. Field 'file' holds an .xlsx or .csv file.
     */
    body: DocumentUploadRequest
  }
): Observable<StrictHttpResponse<{
'message'?: string;
'data'?: ImportUploadResponse;
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerDepartmentService.PostCustomerDepartmentImportUploadPath, 'post');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{
        'message'?: string;
        'data'?: ImportUploadResponse;
        }>;
      })
    );
  }

  /**
   * Upload and validate a filled CustomerDepartment import template (.xlsx or .csv).
   *
   * Validates every row up front — including that each row's location id names a location
   * of THIS customer: any error returns 400 with the full error list and nothing is
   * stored. An already-existing department (same name within the row's location) is NOT an
   * upload error — it is a process-time duplicate whose timestamp gets touched. On success
   * the file is saved and an import record is created (unprocessed) for a later
   * processing call.
   *
   * **Accessible only for customer admins of the customer in the URL**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `postCustomerDepartmentImportUpload$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  postCustomerDepartmentImportUpload(params: {

    /**
     * Customer Id.
     */
    customer: number;
    context?: HttpContext

    /**
     * The filled import template. Field 'file' holds an .xlsx or .csv file.
     */
    body: DocumentUploadRequest
  }
): Observable<{
'message'?: string;
'data'?: ImportUploadResponse;
}> {

    return this.postCustomerDepartmentImportUpload$Response(params).pipe(
      map((r: StrictHttpResponse<{
'message'?: string;
'data'?: ImportUploadResponse;
}>) => r.body as {
'message'?: string;
'data'?: ImportUploadResponse;
})
    );
  }

  /**
   * Path part for operation getCustomerDepartmentImportTemplate
   */
  static readonly GetCustomerDepartmentImportTemplatePath = '/api/web/v1/customers/{customer}/departments/import-template';

  /**
   * Download the CustomerDepartment import template (.xlsx, header row only). Each data
   * row names its parent location by id — the import is customer-scoped, so one file can
   * fill departments across all of the customer's locations.
   *
   * **Accessible only for customer admins of the customer in the URL**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCustomerDepartmentImportTemplate()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerDepartmentImportTemplate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Blob>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerDepartmentService.GetCustomerDepartmentImportTemplatePath, 'get');
    if (params) {
      rb.path('customer', params.customer, {});
    }

    return this.http.request(rb.build({
      responseType: 'blob',
      accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Blob>;
      })
    );
  }

  /**
   * Download the CustomerDepartment import template (.xlsx, header row only). Each data
   * row names its parent location by id — the import is customer-scoped, so one file can
   * fill departments across all of the customer's locations.
   *
   * **Accessible only for customer admins of the customer in the URL**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCustomerDepartmentImportTemplate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerDepartmentImportTemplate(params: {

    /**
     * Customer Id.
     */
    customer: number;
    context?: HttpContext
  }
): Observable<Blob> {

    return this.getCustomerDepartmentImportTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<Blob>) => r.body as Blob)
    );
  }

  /**
   * Path part for operation getCustomerDepartmentInquirySimilar
   */
  static readonly GetCustomerDepartmentInquirySimilarPath = '/api/web/v1/customers/{customer}/departments/{department}/inquiries/similar';

  /**
   * Get list of similar inquiries for a department.
   *
   * **Accessible only for users with permission 'FEATURE_ADMIN_INQUIRY'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCustomerDepartmentInquirySimilar()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerDepartmentInquirySimilar$Response(params: {

    /**
     * Customer ID.
     */
    customer: number;

    /**
     * Department ID.
     */
    department: number;

    /**
     * Start date of the period to filter inquiries
     */
    startDate?: string;

    /**
     * End date of the period to filter inquiries
     */
    endDate?: string;

    /**
     * Filter by shift codes (e.g., fd, nd, sd, zd)
     */
    'shifts[]'?: string;

    /**
     * Filter by qualification IDs
     */
    'qualifications[]'?: any;

    /**
     * Filter by main qualification id
     */
    mainQualification?: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ListApiResponse & {
'data'?: Array<CustomerInquiryListItemResponse>;
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerDepartmentService.GetCustomerDepartmentInquirySimilarPath, 'get');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('department', params.department, {});
      rb.query('startDate', params.startDate, {});
      rb.query('endDate', params.endDate, {});
      rb.query('shifts[]', params['shifts[]'], {"style":"form"});
      rb.query('qualifications[]', params['qualifications[]'], {"style":"form"});
      rb.query('mainQualification', params.mainQualification, {"style":"form"});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ListApiResponse & {
        'data'?: Array<CustomerInquiryListItemResponse>;
        }>;
      })
    );
  }

  /**
   * Get list of similar inquiries for a department.
   *
   * **Accessible only for users with permission 'FEATURE_ADMIN_INQUIRY'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCustomerDepartmentInquirySimilar$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerDepartmentInquirySimilar(params: {

    /**
     * Customer ID.
     */
    customer: number;

    /**
     * Department ID.
     */
    department: number;

    /**
     * Start date of the period to filter inquiries
     */
    startDate?: string;

    /**
     * End date of the period to filter inquiries
     */
    endDate?: string;

    /**
     * Filter by shift codes (e.g., fd, nd, sd, zd)
     */
    'shifts[]'?: string;

    /**
     * Filter by qualification IDs
     */
    'qualifications[]'?: any;

    /**
     * Filter by main qualification id
     */
    mainQualification?: number;
    context?: HttpContext
  }
): Observable<ListApiResponse & {
'data'?: Array<CustomerInquiryListItemResponse>;
}> {

    return this.getCustomerDepartmentInquirySimilar$Response(params).pipe(
      map((r: StrictHttpResponse<ListApiResponse & {
'data'?: Array<CustomerInquiryListItemResponse>;
}>) => r.body as ListApiResponse & {
'data'?: Array<CustomerInquiryListItemResponse>;
})
    );
  }

  /**
   * Path part for operation getCustomerDepartmentListOfDepartments
   */
  static readonly GetCustomerDepartmentListOfDepartmentsPath = '/api/web/v1/customers/{customer}/departments';

  /**
   * Get list of customer departments for a customer.
   *
   * **Accessible only for users with permissions 'FEATURE_VIEW_DEPARTMENTS',
   * 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCustomerDepartmentListOfDepartments()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerDepartmentListOfDepartments$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Filter active or inactive customer departments
     */
    active?: any;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ListApiResponse & {
'data'?: Array<DepartmentListItemResponse>;
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerDepartmentService.GetCustomerDepartmentListOfDepartmentsPath, 'get');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.query('active', params.active, {"style":"form"});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ListApiResponse & {
        'data'?: Array<DepartmentListItemResponse>;
        }>;
      })
    );
  }

  /**
   * Get list of customer departments for a customer.
   *
   * **Accessible only for users with permissions 'FEATURE_VIEW_DEPARTMENTS',
   * 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCustomerDepartmentListOfDepartments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerDepartmentListOfDepartments(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Filter active or inactive customer departments
     */
    active?: any;
    context?: HttpContext
  }
): Observable<ListApiResponse & {
'data'?: Array<DepartmentListItemResponse>;
}> {

    return this.getCustomerDepartmentListOfDepartments$Response(params).pipe(
      map((r: StrictHttpResponse<ListApiResponse & {
'data'?: Array<DepartmentListItemResponse>;
}>) => r.body as ListApiResponse & {
'data'?: Array<DepartmentListItemResponse>;
})
    );
  }

  /**
   * Path part for operation getCustomerDepartmentQualificationsList
   */
  static readonly GetCustomerDepartmentQualificationsListPath = '/api/web/v1/customers/{customer}/departments/{department}/qualifications';

  /**
   * Customer Department Qualifications List.
   *
   * **Accessible only for users with permissions 'FEATURE_VIEW_DEPARTMENTS'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCustomerDepartmentQualificationsList()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerDepartmentQualificationsList$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Department Id.
     */
    department: number;

    /**
     * Filter Qualification Category Id
     */
    category?: number;

    /**
     * Filter Customer Department Qualifications by Tag
     */
    tag?: 'business' | 'medical';

    /**
     * Filter Customer Department Qualifications by search
     */
    search?: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ListApiResponse & {
'data'?: {
'qualifications'?: Array<CustomerDepartmentQualificationsListItemResponse>;
};
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerDepartmentService.GetCustomerDepartmentQualificationsListPath, 'get');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('department', params.department, {});
      rb.query('category', params.category, {"style":"form"});
      rb.query('tag', params.tag, {"style":"form"});
      rb.query('search', params.search, {"style":"form"});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ListApiResponse & {
        'data'?: {
        'qualifications'?: Array<CustomerDepartmentQualificationsListItemResponse>;
        };
        }>;
      })
    );
  }

  /**
   * Customer Department Qualifications List.
   *
   * **Accessible only for users with permissions 'FEATURE_VIEW_DEPARTMENTS'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCustomerDepartmentQualificationsList$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerDepartmentQualificationsList(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Department Id.
     */
    department: number;

    /**
     * Filter Qualification Category Id
     */
    category?: number;

    /**
     * Filter Customer Department Qualifications by Tag
     */
    tag?: 'business' | 'medical';

    /**
     * Filter Customer Department Qualifications by search
     */
    search?: string;
    context?: HttpContext
  }
): Observable<ListApiResponse & {
'data'?: {
'qualifications'?: Array<CustomerDepartmentQualificationsListItemResponse>;
};
}> {

    return this.getCustomerDepartmentQualificationsList$Response(params).pipe(
      map((r: StrictHttpResponse<ListApiResponse & {
'data'?: {
'qualifications'?: Array<CustomerDepartmentQualificationsListItemResponse>;
};
}>) => r.body as ListApiResponse & {
'data'?: {
'qualifications'?: Array<CustomerDepartmentQualificationsListItemResponse>;
};
})
    );
  }

  /**
   * Path part for operation getCustomerDepartmentShow
   */
  static readonly GetCustomerDepartmentShowPath = '/api/web/v1/customers/{customer}/departments/{department}';

  /**
   * Show a customer department.
   *
   * **Accessible only for users with roles 'ROLE_PSP_USER', 'ROLE_CUSTOMER_USER'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCustomerDepartmentShow()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerDepartmentShow$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Department Id.
     */
    department: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<GeneralApiResponse & {
'data'?: CustomerDepartmentResponse;
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerDepartmentService.GetCustomerDepartmentShowPath, 'get');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('department', params.department, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<GeneralApiResponse & {
        'data'?: CustomerDepartmentResponse;
        }>;
      })
    );
  }

  /**
   * Show a customer department.
   *
   * **Accessible only for users with roles 'ROLE_PSP_USER', 'ROLE_CUSTOMER_USER'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCustomerDepartmentShow$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerDepartmentShow(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Department Id.
     */
    department: number;
    context?: HttpContext
  }
): Observable<GeneralApiResponse & {
'data'?: CustomerDepartmentResponse;
}> {

    return this.getCustomerDepartmentShow$Response(params).pipe(
      map((r: StrictHttpResponse<GeneralApiResponse & {
'data'?: CustomerDepartmentResponse;
}>) => r.body as GeneralApiResponse & {
'data'?: CustomerDepartmentResponse;
})
    );
  }

  /**
   * Path part for operation getCustomersDepartmentUsersList
   */
  static readonly GetCustomersDepartmentUsersListPath = '/api/web/v1/customers/{customer}/departments/{department}/users';

  /**
   * List users for specified customer department.
   *
   * **Accessible only for users with roles 'ROLE_PSP_USER', 'ROLE_CUSTOMER_USER'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCustomersDepartmentUsersList()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomersDepartmentUsersList$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Department Id.
     */
    department: number;

    /**
     * User roles to filter them by. Possible values: user, admin
     */
    'role[]'?: any;

    /**
     * Search user by first-last name and email.
     */
    search?: any;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ListApiResponse & {
'data'?: Array<UserItemResponse>;
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerDepartmentService.GetCustomersDepartmentUsersListPath, 'get');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('department', params.department, {});
      rb.query('role[]', params['role[]'], {"style":"form"});
      rb.query('search', params.search, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ListApiResponse & {
        'data'?: Array<UserItemResponse>;
        }>;
      })
    );
  }

  /**
   * List users for specified customer department.
   *
   * **Accessible only for users with roles 'ROLE_PSP_USER', 'ROLE_CUSTOMER_USER'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCustomersDepartmentUsersList$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomersDepartmentUsersList(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Department Id.
     */
    department: number;

    /**
     * User roles to filter them by. Possible values: user, admin
     */
    'role[]'?: any;

    /**
     * Search user by first-last name and email.
     */
    search?: any;
    context?: HttpContext
  }
): Observable<ListApiResponse & {
'data'?: Array<UserItemResponse>;
}> {

    return this.getCustomersDepartmentUsersList$Response(params).pipe(
      map((r: StrictHttpResponse<ListApiResponse & {
'data'?: Array<UserItemResponse>;
}>) => r.body as ListApiResponse & {
'data'?: Array<UserItemResponse>;
})
    );
  }

}
